import React, { useEffect, useState } from "react";
import { Table, Avatar, Typography, Input, Tag, Tooltip, Spin } from "antd";
import {
  SearchOutlined,
  UserOutlined,
  CalendarOutlined,
  EyeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  TeamOutlined,
  HeartOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import moment from "moment";
import { useGetUsersListQuery } from "../../../redux/rtk/apiRtk";
import { motion } from "framer-motion";
import { primaryColor } from "../../../utils/constants";

const { Title, Text } = Typography;

const UserProfile = () => {
  // State
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [animateTable, setAnimateTable] = useState(false);

  // API hooks
  const { data, isLoading, error } = useGetUsersListQuery({
    search: searchText,
    page: currentPage,
    limit: pageSize,
  });

  // Trigger animation after component mounts
  useEffect(() => {
    setTimeout(() => {
      setAnimateTable(true);
    }, 100);
  }, []);

  // Search functionality
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Pagination handler
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Table columns
  const columns = [
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <TeamOutlined style={{ color: primaryColor }} />
          </div>
          <span>ID</span>
        </div>
      ),
      dataIndex: "id",
      key: "id",
      width: "5%",
      render: (_, record, index) => (
        <Text strong>{(currentPage - 1) * pageSize + index + 1}</Text>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <UserOutlined style={{ color: primaryColor }} />
          </div>
          <span>USER</span>
        </div>
      ),
      dataIndex: "name",
      key: "name",
      width: "20%",
      render: (_, record) => (
        <div className="flex items-center">
          {record.profilePic?.url ? (
            <Avatar
              size={40}
              src={record.profilePic.url}
              className="border-2 border-white shadow-sm"
              style={{ boxShadow: `0 0 0 2px ${primaryColor}30` }}
            />
          ) : (
            <Avatar
              size={40}
              icon={<UserOutlined />}
              className="border-2 border-white shadow-sm"
              style={{
                backgroundColor: `${primaryColor}50`,
                color: primaryColor,
              }}
            />
          )}
          <div className="ml-3">
            <Text strong className="block text-gray-800">
              {record.firstName} {record.lastName}
            </Text>
            <div className="flex items-center gap-2 mt-1">
              {record.user?.isOnline && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                  <Text className="text-xs text-green-600">Online</Text>
                </div>
              )}
              {record.user?.isInChat && (
                <Tag size="small" color="blue" className="text-xs px-1 py-0">
                  In Chat
                </Tag>
              )}
            </div>
            {record.about && (
              <Text
                type="secondary"
                className="text-xs block truncate max-w-48 mt-1"
              >
                {record.about}
              </Text>
            )}
            {record.jobTitle && (
              <Tag
                className="text-xs capitalize px-2 py-0 mt-1"
                style={{
                  backgroundColor: `${primaryColor}08`,
                  color: primaryColor,
                  border: `1px solid ${primaryColor}15`,
                }}
              >
                {record.jobTitle}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <MailOutlined style={{ color: primaryColor }} />
          </div>
          <span>CONTACT</span>
        </div>
      ),
      dataIndex: "contact",
      key: "contact",
      width: "20%",
      render: (_, record) => (
        <div className="flex flex-col">
          <div className="flex items-center mb-1">
            <MailOutlined
              style={{ color: primaryColor, fontSize: "12px", marginRight: 4 }}
            />
            <Text className="text-xs">{record.user?.email || "No email"}</Text>
          </div>
          <div className="flex items-center">
            <PhoneOutlined
              style={{ color: primaryColor, fontSize: "12px", marginRight: 4 }}
            />

            <Text className="text-xs">
              {record.user?.phone && record.user.phone !== 0
                ? record.user.phone
                : "No phone"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <EnvironmentOutlined style={{ color: primaryColor }} />
          </div>
          <span>LOCATION</span>
        </div>
      ),
      dataIndex: "city",
      key: "city",
      width: "12%",
      render: (city, record) => (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}10` }}
          >
            <EnvironmentOutlined
              style={{ color: primaryColor, fontSize: "12px" }}
            />
          </div>
          <div>
            <Text>{city || "Not specified"}</Text>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <HeartOutlined style={{ color: primaryColor }} />
          </div>
          <span>PREFERENCES</span>
        </div>
      ),
      dataIndex: "showMe",
      key: "preferences",
      width: "12%",
      render: (showMe, record) => (
        <div>
          {showMe && showMe.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {showMe.map((pref, i) => (
                <Tag
                  key={i}
                  className="text-xs capitalize px-2 py-0"
                  style={{
                    backgroundColor: `${primaryColor}15`,
                    color: primaryColor,
                    border: `1px solid ${primaryColor}30`,
                  }}
                >
                  {pref}
                </Tag>
              ))}
            </div>
          )}
          {record.ageRange && (
            <Text type="secondary" className="text-xs block mt-1">
              Age: {record.ageRange.from}-{record.ageRange.to}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CheckCircleFilled style={{ color: primaryColor }} />
          </div>
          <span>STATUS</span>
        </div>
      ),
      dataIndex: "hideprofile",
      key: "status",
      width: "12%",
      render: (hideprofile, record) => {
        const isBlocked = record.user?.blocked;
        const isVerified = record.user?.verified;

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center">
              {isBlocked ? (
                <>
                  <CloseCircleFilled
                    className="mr-1"
                    style={{ color: "#f5222d" }}
                  />
                  <Text type="danger" className="text-xs">
                    Blocked
                  </Text>
                </>
              ) : (
                <>
                  <CheckCircleFilled
                    className="mr-1"
                    style={{ color: "#52c41a" }}
                  />
                  <Text type="success" className="text-xs">
                    Active
                  </Text>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Tag
                size="small"
                color={isVerified ? "green" : "orange"}
                className="text-xs px-1 py-0"
              >
                {isVerified ? "Verified" : "Unverified"}
              </Tag>
              {hideprofile && (
                <Tag size="small" color="red" className="text-xs px-1 py-0">
                  Hidden
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center w-7 h-7 rounded-full mr-2"
            style={{ background: `${primaryColor}15` }}
          >
            <CalendarOutlined style={{ color: primaryColor }} />
          </div>
          <span>JOINED</span>
        </div>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: "15%",
      render: (date) => (
        <div>
          <Text className="block">{moment(date).format("MMM Do, YYYY")}</Text>
          <Text type="secondary" className="text-xs">
            {moment(date).format("h:mm A")}
          </Text>
        </div>
      ),
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Link to={`/user-details/${record._id}`}>
          <button
            className="flex items-center px-3 py-1.5 rounded-lg transition-all duration-300 hover:shadow-md"
            style={{
              background: primaryColor,
              color: "white",
              boxShadow: `0 2px 8px 0 ${primaryColor}40`,
            }}
          >
            <EyeOutlined className="mr-1" /> View Profile
          </button>
        </Link>
      ),
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="p-4 sm:p-6 lg:p-8"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: animateTable ? 1 : 0,
          y: animateTable ? 0 : 20,
        }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-lg overflow-hidden"
        style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
      >
        {/* Header */}
        <div
          className="py-5 px-6 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
          style={{ borderColor: "rgba(164, 1, 101, 0.1)" }}
        >
          <div className="flex items-center">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full mr-4"
              style={{ background: `${primaryColor}15` }}
            >
              <TeamOutlined style={{ fontSize: 20, color: primaryColor }} />
            </div>
            <div>
              <Title level={4} style={{ margin: 0, color: primaryColor }}>
                User Management
              </Title>
              <Text type="secondary">{data?.total || 0} users found</Text>
            </div>
          </div>

          <div className="custom-search-wrapper" style={{ width: 300 }}>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
              allowClear
              onChange={(e) => handleSearch(e.target.value)}
              className="custom-search-input"
              style={{
                borderRadius: "8px",
                padding: "8px 12px",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="p-0">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Spin
                size="large"
                tip="Loading users..."
                style={{ "--ant-primary-color": primaryColor }}
              />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={
                data?.data?.map((item) => ({
                  ...item,
                  key: item._id,
                })) || []
              }
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: data?.total || 0,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} users`,
                className: "px-6 py-4",
              }}
              onChange={handleTableChange}
              className="custom-table"
              rowClassName={() => "table-row"}
              scroll={{ x: 1000 }}
              locale={{
                emptyText: (
                  <div className="py-8 text-center">
                    <UserOutlined style={{ fontSize: 32, color: "#ccc" }} />
                    <p className="mt-2 text-gray-500">
                      {searchText
                        ? "No users match your search"
                        : "No users found"}
                    </p>
                  </div>
                ),
              }}
            />
          )}
        </div>
      </motion.div>

      {/* Custom styles */}
      <style jsx="true">{`
        .custom-search-input {
          border-color: #e2e8f0;
        }

        .custom-search-input:hover {
          border-color: ${primaryColor}80 !important;
        }

        .custom-search-input:focus,
        .custom-search-input-focused {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .custom-table .ant-table-thead > tr > th {
          background-color: #f9f9f9;
          font-weight: 600;
          color: #333;
          padding: 16px;
        }

        .table-row:hover {
          background-color: ${primaryColor}05 !important;
        }

        .ant-table-row-expand-icon {
          color: ${primaryColor} !important;
        }

        .ant-pagination-item-active {
          border-color: ${primaryColor} !important;
        }

        .ant-pagination-item-active a {
          color: ${primaryColor} !important;
        }

        .ant-select-item-option-selected {
          background-color: ${primaryColor}15 !important;
        }
      `}</style>
    </motion.div>
  );
};

export default UserProfile;
